import React, { useState, useEffect } from 'react';
import { gastoService, especialidadService, uploadService } from '../services/api';
import '../styles/GastosProyecto.css';

const GastosProyecto = ({ proyecto_id, proyectoCompletado = false }) => {
  const [gastos, setGastos] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [gastoEditando, setGastoEditando] = useState(null);
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  const [formData, setFormData] = useState({
    rut_proveedor: '',
    nombre_proveedor: '',
    monto: '',
    especialidad_id: '',
    descripcion: '',
    tipo_documento: 'factura'
  });
  const [imagenes, setImagenes] = useState([]);

  useEffect(() => {
    cargarGastos();
    cargarEspecialidades();
  }, [proyecto_id]);

  const cargarGastos = async () => {
    try {
      const response = await gastoService.getByProyecto(proyecto_id);
      setGastos(response.data);
    } catch (error) {
      console.error('Error al cargar gastos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarEspecialidades = async () => {
    try {
      const response = await especialidadService.getAll({ activo: 'true' });
      setEspecialidades(response.data);
    } catch (error) {
      console.error('Error al cargar especialidades:', error);
    }
  };

  const abrirModal = (gasto = null) => {
    if (gasto) {
      setGastoEditando(gasto);
      setFormData({
        rut_proveedor: gasto.rut_proveedor,
        nombre_proveedor: gasto.nombre_proveedor,
        monto: gasto.monto,
        especialidad_id: gasto.especialidad_id || '',
        descripcion: gasto.descripcion || '',
        tipo_documento: gasto.tipo_documento || 'factura'
      });
      setImagenes(gasto.imagenes || []);
    } else {
      setGastoEditando(null);
      setFormData({
        rut_proveedor: '',
        nombre_proveedor: '',
        monto: '',
        especialidad_id: '',
        descripcion: '',
        tipo_documento: 'factura'
      });
      setImagenes([]);
    }
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setGastoEditando(null);
    setImagenes([]);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const gastoData = {
        proyecto_id,
        ...formData
      };

      if (gastoEditando) {
        await gastoService.update(gastoEditando.id, gastoData);
        alert('Gasto actualizado exitosamente');
      } else {
        const response = await gastoService.create(gastoData);
        const gastoCreado = response.data.gasto;

        // Guardar imágenes si hay alguna
        for (const imagen of imagenes) {
          await gastoService.agregarImagen({
            gasto_id: gastoCreado.id,
            url: imagen.url,
            descripcion: imagen.descripcion || ''
          });
        }

        alert('Gasto creado exitosamente');
      }

      cargarGastos();
      cerrarModal();
    } catch (error) {
      console.error('Error al guardar gasto:', error);
      alert(error.response?.data?.error || 'Error al guardar gasto');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este gasto?')) return;

    try {
      await gastoService.delete(id);
      cargarGastos();
      alert('Gasto eliminado exitosamente');
    } catch (error) {
      console.error('Error al eliminar gasto:', error);
      alert('Error al eliminar gasto');
    }
  };

  const subirImagen = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar que sea imagen
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no debe superar 5MB');
      return;
    }

    setSubiendoImagen(true);
    try {
      const formData = new FormData();
      formData.append('imagen', file);

      const response = await uploadService.subirImagen(formData);
      const nuevaImagen = {
        url: response.data.url,
        descripcion: ''
      };

      // Si está editando, guardar la imagen directamente
      if (gastoEditando) {
        const imagenGuardada = await gastoService.agregarImagen({
          gasto_id: gastoEditando.id,
          url: nuevaImagen.url,
          descripcion: ''
        });
        setImagenes([...imagenes, imagenGuardada.data.imagen]);
        cargarGastos();
      } else {
        // Si es nuevo, solo agregarla a la lista temporal
        setImagenes([...imagenes, nuevaImagen]);
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      alert('Error al subir imagen');
    } finally {
      setSubiendoImagen(false);
    }
  };

  const eliminarImagen = async (imagenIndex, imagen_id = null) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;

    try {
      if (imagen_id) {
        // Es una imagen ya guardada
        await gastoService.eliminarImagen(imagen_id);
        cargarGastos();
      }
      // Eliminar de la lista local
      setImagenes(imagenes.filter((_, index) => index !== imagenIndex));
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      alert('Error al eliminar imagen');
    }
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(monto);
  };

  const calcularTotalGastos = () => {
    return gastos.reduce((total, gasto) => total + parseFloat(gasto.monto || 0), 0);
  };

  if (loading) {
    return <div className="loading">Cargando gastos...</div>;
  }

  return (
    <div className="gastos-proyecto-container">
      <div className="gastos-header">
        <div>
          <h2>Gastos del Proyecto</h2>
          <p className="gastos-total">
            Total: <strong>{formatearMonto(calcularTotalGastos())}</strong>
          </p>
        </div>
        {!proyectoCompletado && (
          <button className="btn btn-primary" onClick={() => abrirModal()}>
            + Agregar Gasto
          </button>
        )}
      </div>

      {gastos.length === 0 ? (
        <div className="empty-state">
          <p>No hay gastos registrados</p>
          {!proyectoCompletado && (
            <button className="btn btn-primary" onClick={() => abrirModal()}>
              Agregar primer gasto
            </button>
          )}
        </div>
      ) : (
        <div className="gastos-lista">
          {gastos.map(gasto => (
            <div key={gasto.id} className="gasto-card">
              <div className="gasto-header">
                <div>
                  <h3>{gasto.nombre_proveedor}</h3>
                  <p className="gasto-rut">RUT: {gasto.rut_proveedor}</p>
                </div>
                <div className="gasto-monto">
                  {formatearMonto(gasto.monto)}
                </div>
              </div>

              <div className="gasto-info">
                <div className="info-row">
                  <span className="label">Tipo:</span>
                  <span className="value">{gasto.tipo_documento}</span>
                </div>
                {gasto.especialidad && (
                  <div className="info-row">
                    <span className="label">Especialidad:</span>
                    <span className="value">{gasto.especialidad}</span>
                  </div>
                )}
                {gasto.descripcion && (
                  <div className="info-row">
                    <span className="label">Descripción:</span>
                    <span className="value">{gasto.descripcion}</span>
                  </div>
                )}
              </div>

              {gasto.imagenes && gasto.imagenes.length > 0 && (
                <div className="gasto-imagenes">
                  <p className="imagenes-titulo">Imágenes adjuntas: {gasto.imagenes.length}</p>
                  <div className="imagenes-mini-grid">
                    {gasto.imagenes.slice(0, 3).map((imagen, index) => (
                      <img
                        key={index}
                        //src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000'}${imagen.url}`}
			src={imagen.url.startsWith('http') ? imagen.url : `${window.location.origin}${imagen.url}`}
                        alt={`Gasto ${index + 1}`}
                        className="imagen-mini"
                        onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000'}${imagen.url}`, '_blank')}
                      />
                    ))}
                    {gasto.imagenes.length > 3 && (
                      <div className="mas-imagenes">+{gasto.imagenes.length - 3}</div>
                    )}
                  </div>
                </div>
              )}

              {!proyectoCompletado && (
                <div className="gasto-actions">
                  <button className="btn btn-secondary" onClick={() => abrirModal(gasto)}>
                    Editar
                  </button>
                  <button className="btn btn-danger" onClick={() => handleEliminar(gasto.id)}>
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal modal-gasto" onClick={(e) => e.stopPropagation()}>
            <h2>{gastoEditando ? 'Editar Gasto' : 'Nuevo Gasto'}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>RUT Proveedor *</label>
                  <input
                    type="text"
                    name="rut_proveedor"
                    value={formData.rut_proveedor}
                    onChange={handleChange}
                    required
                    placeholder="12.345.678-9"
                  />
                </div>

                <div className="form-group">
                  <label>Nombre Proveedor *</label>
                  <input
                    type="text"
                    name="nombre_proveedor"
                    value={formData.nombre_proveedor}
                    onChange={handleChange}
                    required
                    placeholder="Nombre del proveedor"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Monto *</label>
                  <input
                    type="number"
                    name="monto"
                    value={formData.monto}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de Documento</label>
                  <select
                    name="tipo_documento"
                    value={formData.tipo_documento}
                    onChange={handleChange}
                  >
                    <option value="factura">Factura</option>
                    <option value="boleta">Boleta</option>
                    <option value="ticket">Ticket</option>
                    <option value="otros">Otros</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Especialidad Asociada</label>
                <select
                  name="especialidad_id"
                  value={formData.especialidad_id}
                  onChange={handleChange}
                >
                  <option value="">Sin especialidad</option>
                  {especialidades.map(esp => (
                    <option key={esp.id} value={esp.id}>
                      {esp.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Descripción del gasto"
                />
              </div>

              <div className="form-group">
                <label>Fotografías del Gasto</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={subirImagen}
                  disabled={subiendoImagen || proyectoCompletado}
                />
                {subiendoImagen && <p className="subiendo">Subiendo imagen...</p>}

                {imagenes.length > 0 && (
                  <div className="imagenes-preview">
                    {imagenes.map((imagen, index) => (
                      <div key={index} className="imagen-preview-item">
                        <img
                          //src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000'}${imagen.url}`}
			  src={imagen.url.startsWith('http') ? imagen.url : `${window.location.origin}${imagen.url}`}
                          alt={`Imagen ${index + 1}`}
                        />
                        {!proyectoCompletado && (
                          <button
                            type="button"
                            className="btn-eliminar-img"
                            onClick={() => eliminarImagen(index, imagen.id)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {gastoEditando ? 'Actualizar' : 'Crear'} Gasto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GastosProyecto;
